import React, { useState } from "react";
import logger from "../lib/logger";
import { motion } from "framer-motion";
import { Eyebrow, Btn } from "../components/Primitives";
import { api } from "../lib/api";
import { useAccount } from "../account/AccountContext";
import SignupConsent, { isUnder18, UNDERAGE_MESSAGE } from "../components/SignupConsent";
import PasswordInput from "../components/PasswordInput";
import LocationInput from "../components/LocationInput";
import { toast } from "sonner";

export default function EmailCapture({ user, onCaptured, onBack }) {
    const { setPassword: createAccount, account } = useAccount();
    const [email, setEmail] = useState(user?.email || "");
    const [pw, setPw] = useState("");
    const [dob, setDob] = useState("");
    const [location, setLocation] = useState(user?.location || null);
    const [agreed, setAgreed] = useState(false);
    const [wantAccount, setWantAccount] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const pwOk = !wantAccount || pw.length >= 8;
    const creatingAccount = wantAccount && !account;
    const consentOk = !creatingAccount || (agreed && dob && !isUnder18(dob));

    const submit = async (e) => {
        e?.preventDefault();
        if (!valid) { setError("Please enter a valid email."); return; }
        if (!location) { setError("Please pick your city from the suggestions."); return; }
        if (wantAccount && !pwOk) { setError("Password must be at least 8 characters."); return; }
        if (creatingAccount && isUnder18(dob)) { setError(UNDERAGE_MESSAGE); return; }
        setSubmitting(true);
        setError("");
        try {
            await api.saveLocation(user.session_id, location);
            await api.captureEmail(user.session_id, email.trim());
            if (creatingAccount) {
                try {
                    await createAccount(email.trim(), pw, { date_of_birth: dob, accepted_terms: agreed });
                    await api.acceptTerms(user.session_id, "signup").catch(err => logger.warn("acceptTerms persist failed:", err));
                    toast.success("Account created — you can sign in from any device.");
                } catch (err) {
                    const detail = err?.response?.data?.detail;
                    if (err?.response?.status === 403) {
                        setError(typeof detail === "string" ? detail : UNDERAGE_MESSAGE);
                        setSubmitting(false);
                        return;
                    }
                    if (err?.response?.status === 409) {
                        toast.message("Email already has an account — sign in to link this assessment.");
                    } else {
                        logger.warn("set-password failed:", detail);
                    }
                }
            }
            onCaptured(email.trim());
        } catch (err) {
            const msg = err.response?.data?.detail || "Could not save. Try again.";
            setError(typeof msg === "string" ? msg : "Could not save.");
            toast.error("Could not save your email");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="email-capture-screen">
            <div className="px-6 pt-6 pb-3 flex items-center justify-between">
                <button
                    data-testid="email-back-btn"
                    onClick={onBack}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    Almost there
                </span>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1 flex flex-col justify-center px-7"
            >
                <Eyebrow className="mb-4">Before we unlock</Eyebrow>
                <h1 className="font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy tracking-tight">
                    <span className="italic">Where</span> should we send it?
                </h1>
                <p className="mt-4 font-display italic text-lg text-ink-muted leading-snug">
                    Your full archetype profile, a saved link, and occasional letters from us. No spam — only what's worth your time.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4">
                    <input
                        data-testid="email-input"
                        type="email"
                        autoFocus
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(""); }}
                        className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-3 font-display text-2xl text-burgundy placeholder:text-ink-light/50 focus:outline-none"
                    />

                    <div className="pt-2">
                        <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">Where are you based?</div>
                        <LocationInput value={location} onChange={(v) => { setLocation(v); setError(""); }} idPrefix="capture-location" />
                    </div>

                    <label className="flex items-start gap-2 cursor-pointer pt-3" data-testid="email-create-account">
                        <input
                            type="checkbox"
                            checked={wantAccount}
                            onChange={e => setWantAccount(e.target.checked)}
                            className="mt-0.5 w-5 h-5 shrink-0 accent-burgundy"
                        />
                        <span className="text-sm text-ink leading-snug">
                            <span className="font-semibold text-burgundy">Create an account</span> so I can sign in from any device and see my results later. <span className="text-ink-muted">(recommended)</span>
                        </span>
                    </label>

                    {wantAccount && (
                        <div className="space-y-4 pl-6" data-testid="email-password-row">
                            <div className="space-y-1">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Password (8+ chars)</div>
                                <PasswordInput
                                    testId="email-password"
                                    autoComplete="new-password"
                                    value={pw}
                                    onChange={e => { setPw(e.target.value); setError(""); }}
                                    className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-2.5 px-3 text-base text-ink focus:outline-none"
                                />
                            </div>
                            {!account && (
                                <SignupConsent dob={dob} setDob={(v) => { setDob(v); setError(""); }} agreed={agreed} setAgreed={setAgreed} idPrefix="signup" />
                            )}
                        </div>
                    )}

                    {error && (
                        <div data-testid="email-error" className="text-sm text-burgundy">
                            {error}
                        </div>
                    )}
                </form>

                <div className="mt-6 editorial-rule" />

                <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-ink-light">
                    We use your email to send your profile and account notices, and your city to plan where Ever After launches next.
                </p>
            </motion.div>

            <div className="sticky bottom-0 bg-cream/95 backdrop-blur-md px-6 pb-8 pt-3 pb-safe space-y-3 z-20">
                <Btn
                    data-testid="email-submit-btn"
                    onClick={submit}
                    disabled={!valid || !location || !pwOk || !consentOk || submitting}
                    className={!valid || !location || !pwOk || !consentOk || submitting ? "opacity-50 cursor-not-allowed" : ""}
                >
                    {submitting ? "Saving…" : "Continue to unlock"}
                </Btn>
                <p className="text-center text-[9px] tracking-[0.28em] uppercase text-ink-light pt-1">
                    By continuing, you agree to our{" "}
                    <a href="/terms" target="_blank" rel="noopener" data-testid="email-terms-link" className="tap-link underline underline-offset-4 hover:text-burgundy">
                        terms
                    </a>{" "}
                    &{" "}
                    <a href="/privacy" target="_blank" rel="noopener" data-testid="email-privacy-link" className="tap-link underline underline-offset-4 hover:text-burgundy">
                        privacy policy
                    </a>.
                </p>
            </div>
        </div>
    );
}
